import { Add, Delete } from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../../../../services/api";
import { notifyOnPage } from "../../../../utils/notifyOnPage";

export interface HostItem {
  host: string;
  item: string[];
}
interface HostErrorType {
  perfilIdx: number;
  variavelIdx: number;
  hostIdx: number;
  message: string;
  field?: "host" | "item" | "custom";
}

interface ConfigureHostsProps {
  hostData: HostDataType[];
  setHostData: Dispatch<SetStateAction<HostDataType[]>>;
  relatorioEditData?: any;
  isEditDataProcessed?: boolean;
  setIsEditDataProcessed?: Dispatch<SetStateAction<boolean>>;
  errors?: HostErrorType[];
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const ConfigureHosts = ({
  hostData,
  setHostData,
  relatorioEditData,
  isEditDataProcessed = false,
  setIsEditDataProcessed,
  errors,
}: ConfigureHostsProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [hosts, setHosts] = useState<{ id: string; nome: string }[]>([]);
  const [itemsLoadingHostId, setItemsLoadingHostId] = useState<string | null>(
    null,
  );
  const [avaibleItems, setAvaibleItems] = useState<
    Map<string, { itemId: string; name: string }[]>
  >(new Map());

  const hasAggreate = useRef(new Map<string, boolean>());
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchHosts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/easyReport/HostName/find");
        setHosts(response.data);
      } catch (error: any) {
        notifyOnPage(
          window.location.pathname,
          error?.response?.data?.error || "Erro ao carregar hosts",
          "error",
          3000,
        );
      } finally {
        setLoading(false);
      }
    };
    fetchHosts();

    const hostRequests: {
      hostId: string;
      item: { key: string; aplicacaoName: string }[];
    }[] = [];

    hostData.forEach((perfil) => {
      perfil.variaveis?.forEach((variavel) => {
        if (variavel.titulo === "Agregar hosts") {
          hasAggreate.current.set(perfil.perfilId, false);
        }
        variavel.hosts?.forEach((host) => {
          if (host.host && variavel.item) {
            hostRequests.push({ hostId: host.host, item: variavel.item });
          }
        });
      });
    });

    hostRequests.forEach(async ({ hostId, item }) => {
      const compositeKey = getCompositeKey(hostId, item);
      if (!avaibleItems.has(compositeKey)) {
        try {
          const items = await api.post("/easyReport/v2/findItemsHostsKey", {
            hostId,
            key_: item.map((k) => k.key),
          });
          setAvaibleItems((prev) => {
            const newMap = new Map(prev);
            newMap.set(compositeKey, items.data);
            return newMap;
          });
        } catch (error: any) {
          notifyOnPage(
            window.location.pathname,
            error?.response?.data?.error || "Erro ao carregar items",
            "error",
            3000,
          );
        }
      }
    });
  }, []);

  // Carrega os dados de edição
  useEffect(() => {
    if (!relatorioEditData || isEditDataProcessed) return;
    const loadEditData = async () => {
      try {
        const newHostData: HostDataType[] = [];
        const loadedProfileIds = new Set<string>();

        for (const profile of relatorioEditData.profiles) {
          const matchingPerfil = hostData.find(
            (h) => h.perfilId === profile.id,
          );
          if (!matchingPerfil) continue;
          loadedProfileIds.add(profile.id);

          const updatedVariaveis = await Promise.all(
            matchingPerfil.variaveis?.map(async (variavel) => {
              const editVariavel = profile.variaveis?.find(
                (v: any) => v.id === variavel.id,
              );
              if (!editVariavel) return variavel;
              const updatedHosts = await Promise.all(
                editVariavel.hosts.map(async (editHost: any) => {
                  const compositeKey = getCompositeKey(
                    editHost.hostId.toString(),
                    variavel.item || [],
                  );
                  if (!avaibleItems.has(compositeKey)) {
                    try {
                      const items = await api.post(
                        "/easyReport/v2/findItemsHostsKey",
                        {
                          hostId: editHost.hostId.toString(),
                          key_: variavel.item.map((k) => k.key),
                        },
                      );
                      setAvaibleItems((prev) => {
                        const newMap = new Map(prev);
                        newMap.set(compositeKey, items.data);
                        return newMap;
                      });
                    } catch (error: any) {
                      notifyOnPage(
                        window.location.pathname,
                        error?.response?.data?.error ||
                          "Erro ao carregar items",
                        "error",
                        3000,
                      );
                    }
                  }
                  return {
                    host: editHost.hostId.toString(),
                    item: editHost.items.map((itemId: number) =>
                      itemId.toString(),
                    ),
                  };
                }),
              );
              return {
                ...variavel,
                hosts: updatedHosts,
              };
            }) || [],
          );
          newHostData.push({
            ...matchingPerfil,
            variaveis: updatedVariaveis,
          });
        }
        // Adiciona os perfis adicionados antes da inicialização
        const newProfiles = hostData.filter(
          (h) => !loadedProfileIds.has(h.perfilId),
        );
        newHostData.push(...newProfiles);

        if (newHostData.length > 0) {
          setHostData(newHostData);
          if (setIsEditDataProcessed) {
            setIsEditDataProcessed(true);
          }
        }
      } catch (error: any) {
        notifyOnPage(
          window.location.pathname,
          error?.response?.data?.error || "Erro ao carregar dados do relatório",
          "error",
          3000,
        );
      }
    };
    loadEditData();
  }, [relatorioEditData, isEditDataProcessed]);

  function getCompositeKey(hostId: string, items: { key: string }[]): string {
    const itemKeys = items
      .map((k) => k.key)
      .sort()
      .join(",");
    return `${hostId}:${itemKeys}`;
  }

  const avaibleHosts = useMemo(() => hosts, [hosts]);

  const handleAddHost = (perfilIdx: number, variavelIdx: number) => {
    setHostData((prev) =>
      prev.map((perfil, idx) =>
        idx === perfilIdx
          ? {
              ...perfil,
              variaveis: perfil.variaveis?.map((variavel, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...variavel,
                      hosts: [
                        ...(variavel.hosts || []),
                        { host: "", item: [] },
                      ],
                    }
                  : variavel,
              ),
            }
          : perfil,
      ),
    );
  };

  const handleRemoveHost = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
  ) => {
    setHostData((prev) =>
      prev.map((perfil, idx) =>
        idx === perfilIdx
          ? {
              ...perfil,
              variaveis: perfil.variaveis?.map((variavel, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...variavel,
                      hosts:
                        variavel.hosts?.filter((_, i) => i !== hostIdx) || [],
                    }
                  : variavel,
              ),
            }
          : perfil,
      ),
    );
  };

  const styles = {
    container: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    title: {
      fontSize: "1.5rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    subtitle: {
      fontSize: "16px",
      color: theme.palette.text.secondary,
    },
    header: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
    },
    boxInput: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      width: "100%",
      gap: 0,
      minWidth: 0,
    },
    boxHost: {
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      width: "100%",
      gap: 2,
    },
    titleInput: {
      fontSize: "1rem",
      fontWeight: 500,
    },
    boxItemAndDelete: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 1,
    },
    addButton: { height: "40px", width: "fit-content", paddingBottom: 2 },
    textField: { width: "100%" },
    perfilTitle: {
      fontWeight: 600,
      mb: 1,
      fontSize: "1.2rem",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "300px",
      display: "block",
    },
    perfilBox: { mb: 1 },
    variavelBox: { pl: 2 },
    variavelTitle: {
      fontWeight: 500,
      mb: 1,
      color: theme.palette.text.secondary,
      fontSize: "1rem",
      textTransform: "capitalize",
    },
  };

  const handleCustomInput = (
    perfilIdx: number,
    variavelIdx: number,
    newValue: string,
  ) => {
    setHostData((prev) =>
      prev.map((p, pIdx) =>
        pIdx === perfilIdx
          ? {
              ...p,
              variaveis: p.variaveis?.map((v, vIdx) => {
                if (v.titulo === "Agregar hosts") {
                  hasAggreate.current.set(p.perfilId, newValue === "1");
                }
                return vIdx === variavelIdx
                  ? {
                      ...v,
                      hosts: v.hosts?.map((h) => ({
                        ...h,
                        host: newValue ? newValue : "",
                        item: ["1"],
                      })),
                    }
                  : v;
              }),
            }
          : p,
      ),
    );
  };

  const handleHostChange = async (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    newValue: { id: string; nome: string } | null,
    item: { key: string; aplicacaoName: string }[],
  ) => {
    setHostData((prev) =>
      prev.map((p, pIdx) =>
        pIdx === perfilIdx
          ? {
              ...p,
              variaveis: p.variaveis?.map((v, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...v,
                      hosts: v.hosts?.map((h, hIdx) =>
                        hIdx === hostIdx
                          ? {
                              ...h,
                              host: newValue ? newValue.id : "",
                            }
                          : h,
                      ),
                    }
                  : v,
              ),
            }
          : p,
      ),
    );
    if (!newValue) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    const signal = controller.signal;

    abortControllerRef.current = controller;
    try {
      setItemsLoadingHostId(newValue.id);
      const items = await api.post(
        "/easyReport/v2/findItemsHostsKey",
        {
          hostId: newValue.id,
          key_: item.map((k) => k.key),
        },
        { signal },
      );
      setAvaibleItems((prev) => {
        const newMap = new Map(prev);
        const compositeKey = getCompositeKey(newValue.id, item);
        newMap.set(compositeKey, items.data);
        return newMap;
      });
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") return;
      notifyOnPage(
        window.location.pathname,
        error?.response?.data?.error || "Erro ao carregar items",
        "error",
        3000,
      );
    } finally {
      setItemsLoadingHostId(null);
    }
  };

  const handleItemChange = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    newValue: { itemId: string; name: string }[],
  ) => {
    setHostData((prev) =>
      prev.map((perfil, pIdx) =>
        pIdx === perfilIdx
          ? {
              ...perfil,
              variaveis: perfil.variaveis?.map((variavel, vIdx) =>
                vIdx === variavelIdx
                  ? {
                      ...variavel,
                      hosts: variavel.hosts?.map((host, hIdx) =>
                        hIdx === hostIdx
                          ? {
                              ...host,
                              item: newValue.map((item) => item.itemId),
                            }
                          : host,
                      ),
                    }
                  : variavel,
              ),
            }
          : perfil,
      ),
    );
  };

  const getFieldError = (
    perfilIdx: number,
    variavelIdx: number,
    hostIdx: number,
    field?: "host" | "item" | "custom",
  ) => {
    if (!errors || errors.length === 0) return undefined;
    if (!field) return undefined;
    return errors.find(
      (e) =>
        e.perfilIdx === perfilIdx &&
        e.variavelIdx === variavelIdx &&
        e.hostIdx === hostIdx &&
        e.field === field,
    );
  };
  return (
    <Box sx={styles.container}>
      {hostData.map((perfil, perfilIdx) => (
        <>
          <Box key={perfil.perfilId} sx={styles.perfilBox}>
            <Typography sx={styles.perfilTitle}>
              {perfil.perfilTitle}
            </Typography>

            {perfil.variaveis?.some(
              (v) => v.item?.[0].aplicacaoName === "CUSTOM",
            ) && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  {perfil.variaveis?.map((variavel, variavelIdx) => {
                    if (variavel.item[0].aplicacaoName !== "CUSTOM") return;
                    const customError = getFieldError(
                      perfilIdx,
                      variavelIdx,
                      0,
                      "custom",
                    );
                    return (
                      <Box key={variavel.id} sx={styles.variavelBox}>
                        <Typography sx={styles.variavelTitle}>
                          {variavel.titulo}
                        </Typography>
                        {variavel.item[0]?.key === "boolean" ? (
                          <FormControl error={!!customError}>
                            <RadioGroup
                              sx={{ display: "flex", alignItems: "center" }}
                              row
                              value={
                                variavel.hosts?.[0]?.host === "1"
                                  ? "1"
                                  : variavel.hosts?.[0]?.host === "0"
                                    ? "0"
                                    : ""
                              }
                              onChange={(e) =>
                                handleCustomInput(
                                  perfilIdx,
                                  variavelIdx,
                                  e.target.value,
                                )
                              }
                            >
                              <FormControlLabel
                                value="1"
                                control={<Radio />}
                                label="Verdadeiro"
                              />
                              <FormControlLabel
                                value="0"
                                control={<Radio />}
                                label="Falso"
                              />
                            </RadioGroup>
                            {customError && (
                              <FormHelperText>
                                {customError.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        ) : (
                          <TextField
                            value={variavel.hosts?.[0]?.host ?? ""}
                            onChange={(e) => {
                              let value = e.target.value.replace(/,/g, ".");
                              if (/^(\d+(\.\d*)?|\.\d*)?$/.test(value)) {
                                handleCustomInput(
                                  perfilIdx,
                                  variavelIdx,
                                  value,
                                );
                              }
                            }}
                            size="small"
                            fullWidth
                            error={!!customError}
                            helperText={customError?.message}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Divider sx={{ marginY: 2 }} />
              </>
            )}

            {/* Non-CUSTOM hosts/items */}
            {perfil.variaveis?.map((variavel, variavelIdx) => {
              if (variavel.item?.[0].aplicacaoName === "CUSTOM") return;
              return (
                <Box key={variavel.id} sx={styles.variavelBox}>
                  <Typography sx={styles.variavelTitle}>
                    {variavel.titulo}
                  </Typography>
                  {variavel.hosts?.map((host, hostIdx) => {
                    // Resolve errors for Host and Item fields
                    const hostError = getFieldError(
                      perfilIdx,
                      variavelIdx,
                      hostIdx,
                      "host",
                    );
                    const itemError = getFieldError(
                      perfilIdx,
                      variavelIdx,
                      hostIdx,
                      "item",
                    );

                    return (
                      <Box sx={styles.boxHost} key={hostIdx + "host"}>
                        {/* Host input */}
                        <Box sx={{ ...styles.boxInput, flex: 1, minWidth: 0 }}>
                          <Typography sx={styles.titleInput}>
                            {"Host"}
                          </Typography>
                          <Box sx={{ position: "relative" }}>
                            <Autocomplete
                              value={
                                avaibleHosts.find((h) => h.id === host.host) ||
                                null
                              }
                              disableCloseOnSelect
                              options={avaibleHosts}
                              getOptionLabel={(option) => option.nome}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              loading={loading}
                              sx={{ minWidth: 0 }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  error={!!hostError}
                                  helperText={hostError?.message}
                                  inputProps={{
                                    ...params.inputProps,
                                    style: {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              )}
                              onChange={(_, newValue) => {
                                handleHostChange(
                                  perfilIdx,
                                  variavelIdx,
                                  hostIdx,
                                  newValue,
                                  variavel.item,
                                );
                              }}
                            />
                            <Fade in={loading} unmountOnExit>
                              <LinearProgress
                                sx={{
                                  height: "2px",
                                  position: "absolute",
                                  top: "0",
                                }}
                              />
                            </Fade>
                          </Box>
                        </Box>

                        {/* Item input */}
                        <Box sx={styles.boxInput}>
                          <Typography sx={styles.titleInput}>
                            {"Item"}
                          </Typography>
                          <Box sx={styles.boxItemAndDelete}>
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                              }}
                            >
                              <Autocomplete
                                multiple
                                disableCloseOnSelect
                                value={(() => {
                                  const compositeKey = getCompositeKey(
                                    host.host,
                                    variavel.item || [],
                                  );
                                  const items =
                                    avaibleItems.get(compositeKey) || [];
                                  return items.filter((item) =>
                                    host.item.includes(item.itemId),
                                  );
                                })()}
                                limitTags={1}
                                size="small"
                                options={(() => {
                                  const compositeKey = getCompositeKey(
                                    host.host,
                                    variavel.item || [],
                                  );
                                  return avaibleItems.get(compositeKey) || [];
                                })()}
                                getOptionLabel={(option) => option.name}
                                loading={itemsLoadingHostId === host.host}
                                //  sx={{ minWidth: 0 }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    //size="small"
                                    disabled={loading}
                                    error={!!itemError}
                                    helperText={itemError?.message}
                                    sx={{
                                      backgroundColor: loading
                                        ? theme.palette.color.stroke
                                        : "inherit",
                                      minWidth: 0,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    inputProps={{
                                      ...params.inputProps,
                                      style: {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      },
                                    }}
                                  />
                                )}
                                onChange={(_, newValue) => {
                                  if (newValue) {
                                    handleItemChange(
                                      perfilIdx,
                                      variavelIdx,
                                      hostIdx,
                                      newValue,
                                    );
                                  }
                                }}
                                renderOption={(props, option, { selected }) => {
                                  const { key, ...optionProps } = props;
                                  return (
                                    <>
                                      <li key={key} {...optionProps}>
                                        <Checkbox
                                          icon={icon}
                                          checkedIcon={checkedIcon}
                                          style={{ marginRight: 8 }}
                                          checked={selected}
                                        />
                                        {option.name}
                                      </li>
                                      <Divider />
                                    </>
                                  );
                                }}
                              />
                              <Fade
                                in={itemsLoadingHostId === host.host || loading}
                                unmountOnExit
                              >
                                <LinearProgress
                                  sx={{
                                    marginTop: itemError ? "-24px" : "0",
                                    height: "2px",
                                  }}
                                />
                              </Fade>
                            </Box>

                            {variavel.hosts && variavel.hosts.length > 1 && (
                              <IconButton
                                onClick={() =>
                                  handleRemoveHost(
                                    perfilIdx,
                                    variavelIdx,
                                    hostIdx,
                                  )
                                }
                              >
                                <Delete color="error" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

                  <Box sx={styles.boxInput}>
                    {variavel.multiple &&
                      hasAggreate.current.get(perfil.perfilId) !== false && (
                        <Button
                          startIcon={<Add />}
                          size="medium"
                          sx={styles.addButton}
                          onClick={() => {
                            handleAddHost(perfilIdx, variavelIdx);
                          }}
                        >
                          Adicionar Host
                        </Button>
                      )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Divider />
        </>
      ))}
    </Box>
  );
};
//export default ConfigureHosts;
